// RUTA: src/components/fitness/CoachStreakCard.tsx
//
// Card de racha personalizada por el COACH del cliente.
//
// Diseño: el mensaje motivacional debe LLAMAR LA ATENCIÓN. Por eso
// todos los tiers tienen color cálido (gradiente naranja/ámbar/oro
// según el nivel) — nunca un card gris plano que se pierda con el
// resto del contenido.
//
// Estructura:
//   [Avatar coach con anillo de color]   MARCO DICE
//                                         "Mensaje motivacional..."
//                                                            ┌──┐
//                                                            │ N│ días
//                                                            └──┘
//
// El número de la racha va en un badge circular destacado a la
// derecha. Conforme el tier sube, el badge intensifica color y
// agrega "shine" / shadow.

import { useEffect, useRef, useState } from 'react';
import { Flame } from 'lucide-react';
import { UserAvatar } from './UserAvatar';
import { useCoachInfo } from '@/hooks/useCoachInfo';
import { getStreakDisplay } from '@/lib/streakMessages';
import { CountUp } from '@/components/ui/CountUp';
import marcoAvatar from '@/assets/marco-avatar-circle.png';
import {
  celebrateOnce,
  fireMilestoneConfetti,
  isStreakMilestone,
  todayKey,
} from '@/lib/celebrate';

interface Props {
  streak: number;
  activity: 'nutrition' | 'cardio';
  clientId?: string | null;
  clientFirstName?: string;
  hideAvatar?: boolean;
  loggedToday?: boolean;
}

// Estilos del wrapper del card por tier — siempre con color cálido,
// incluso al inicio. La idea: que ninguna racha se vea aburrida.
function cardClassesByTier(tier: number): string {
  const base = 'rounded-xl border-2 p-4 transition-all relative overflow-hidden';
  switch (tier) {
    case 0:
      // Sin racha aún — invita a empezar con un toque cálido
      return `${base} border-orange-300/40 bg-gradient-to-br from-orange-50/60 to-amber-50/40 dark:from-orange-950/20 dark:to-amber-950/15`;
    case 1:
      // 1-2 días — naranja suave, ya empezó la racha
      return `${base} border-orange-400/50 bg-gradient-to-br from-orange-50 to-amber-50/80 dark:from-orange-950/30 dark:to-amber-950/20 shadow-sm`;
    case 2:
      // 3-6 días — naranja medio, se siente con energía
      return `${base} border-orange-500/60 bg-gradient-to-br from-orange-100/70 to-amber-100/50 dark:from-orange-950/40 dark:to-amber-950/30 shadow-md shadow-orange-500/10`;
    case 3:
      // 7-13 días — naranja fuerte, una semana es logro
      return `${base} border-orange-500/70 bg-gradient-to-br from-orange-100 to-amber-100/80 dark:from-orange-950/50 dark:to-amber-900/30 shadow-md shadow-orange-500/15`;
    case 4:
      // 14-29 días — ámbar, dos semanas es hábito formado
      return `${base} border-amber-500/70 bg-gradient-to-br from-amber-100 to-yellow-100/80 dark:from-amber-950/50 dark:to-yellow-900/30 shadow-lg shadow-amber-500/20`;
    case 5:
      // 30+ días — oro premium con glow
      return `${base} border-yellow-500/80 bg-gradient-to-br from-yellow-100 via-amber-100 to-orange-100 dark:from-yellow-950/50 dark:via-amber-900/40 dark:to-orange-950/40 shadow-xl shadow-yellow-500/25`;
    default:
      return `${base} border-orange-300/40 bg-card`;
  }
}

// Color del número de la racha (badge circular)
function badgeClassesByTier(tier: number): string {
  switch (tier) {
    case 0:
      return 'bg-orange-200/50 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300';
    case 1:
      return 'bg-orange-400 text-white shadow-md shadow-orange-400/40';
    case 2:
      return 'bg-orange-500 text-white shadow-md shadow-orange-500/40';
    case 3:
      return 'bg-orange-600 text-white shadow-lg shadow-orange-500/40';
    case 4:
      return 'bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/40';
    case 5:
      return 'bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500 text-white shadow-xl shadow-yellow-500/50';
    default:
      return 'bg-muted text-foreground';
  }
}

// Color del ícono Flame y "Marco dice"
function accentColorByTier(tier: number): string {
  switch (tier) {
    case 0:
    case 1:
      return 'text-orange-500';
    case 2:
    case 3:
      return 'text-orange-600 dark:text-orange-400';
    case 4:
      return 'text-amber-600 dark:text-amber-400';
    case 5:
      return 'text-yellow-600 dark:text-yellow-400';
    default:
      return 'text-orange-500';
  }
}

// Anillo decorativo alrededor del avatar (escala con tier)
function avatarRingByTier(tier: number): string {
  switch (tier) {
    case 0:
    case 1:
      return 'ring-2 ring-orange-300/60 dark:ring-orange-500/40';
    case 2:
    case 3:
      return 'ring-2 ring-orange-500/60 dark:ring-orange-400/60';
    case 4:
      return 'ring-2 ring-amber-500/70 dark:ring-amber-400/70';
    case 5:
      return 'ring-[3px] ring-yellow-500/80 dark:ring-yellow-400/80';
    default:
      return '';
  }
}

export function CoachStreakCard({
  streak,
  activity,
  clientId,
  clientFirstName,
  hideAvatar = false,
  loggedToday = true,
}: Props) {
  const { coach } = useCoachInfo(clientId);

  const today = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  })();

  const info = getStreakDisplay(streak, {
    clientFirstName,
    activity,
    loggedToday,
    seed: `${clientId || 'anon'}-${activity}-${today}-${streak}-${loggedToday}`,
  });

  // Celebrate when crossing a streak milestone (3, 7, 14, 30, 60, 100).
  // Use sessionStorage gating so it dispara solo 1 vez por día por tipo.
  // Pulse animation on the card + confetti.
  const [pulse, setPulse] = useState(false);
  const lastStreakRef = useRef<number>(streak);
  useEffect(() => {
    const prev = lastStreakRef.current;
    lastStreakRef.current = streak;
    if (streak > 0 && isStreakMilestone(streak)) {
      const key = `streak-${activity}-${streak}-${todayKey()}-${clientId || 'anon'}`;
      celebrateOnce(key, () => {
        setPulse(true);
        fireMilestoneConfetti();
        // Clear pulse after animation completes (~2.4s = 2 cycles)
        setTimeout(() => setPulse(false), 2500);
      });
    }
    void prev;
  }, [streak, activity, clientId]);

  return (
    <div className={`${cardClassesByTier(info.tier)} ${pulse ? 'animate-pulse-celebrate' : ''}`}>
      {/* Detalle decorativo: pequeña flama de fondo a la derecha,
       *  muy sutil. Solo aparece en tiers altos. */}
      {info.tier >= 3 && (
        <Flame
          className={`absolute -right-2 -top-2 w-24 h-24 opacity-5 pointer-events-none ${accentColorByTier(info.tier)}`}
          aria-hidden="true"
        />
      )}

      <div className="flex items-center gap-3 relative">
        {/* Avatar con anillo de color (o flama si no hay coach).
         *  Si el coach es Marco y no tiene avatar_url en BD, caemos al
         *  asset local marco-avatar-circle.png — mismo patrón que el
         *  CoachHubCard. Garantiza consistencia visual. */}
        {!hideAvatar && coach && (
          <div className={`shrink-0 rounded-full ${avatarRingByTier(info.tier)}`}>
            <UserAvatar
              userId={coach.id}
              name={coach.name}
              avatarUrl={coach.avatarUrl ?? (/marco/i.test(coach.name) ? marcoAvatar : undefined)}
              size="md"
            />
          </div>
        )}
        {(!coach || hideAvatar) && (
          <div className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${avatarRingByTier(info.tier)} bg-card`}>
            <Flame className={`h-6 w-6 ${accentColorByTier(info.tier)}`} />
          </div>
        )}

        {/* Mensaje */}
        <div className="min-w-0 flex-1">
          <div className={`text-[10px] font-black uppercase tracking-widest mb-1 ${accentColorByTier(info.tier)}`}>
            {coach
              ? `${coach.name.split(/\s+/)[0]} dice`
              : activity === 'cardio'
                ? 'Tu racha de cardio'
                : 'Tu racha de logueo'}
          </div>
          <p className="text-sm font-bold leading-snug text-foreground">
            {info.message}
          </p>
        </div>

        {/* Badge circular con el número de la racha */}
        <div className="shrink-0 flex flex-col items-center">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center font-black text-xl ${badgeClassesByTier(info.tier)}`}>
            <CountUp value={streak} />
          </div>
          <div className={`text-[9px] font-black uppercase tracking-widest mt-1 ${accentColorByTier(info.tier)}`}>
            {streak === 1 ? 'día' : 'días'}
          </div>
        </div>
      </div>
    </div>
  );
}
